/**
 * Per-remote-track WebAudio router for the LiveKit-backed audio plane.
 *
 * Mirrors the [livekit-examples/spatial-audio](https://github.com/livekit-examples/spatial-audio/blob/main/src/controller/SpatialAudioController.tsx)
 * pattern — every remote track gets a `PannerNode` and the controller
 * keeps each panner's position in sync with the speaker's world
 * coordinates. Distance + facing translate to attenuation.
 *
 * Two falloff profiles:
 *   * Voice (`Track.Source.Microphone`) — close, sharp, ≤ `say_radius`.
 *   * Music (`publication.trackName === "music"`) — wider, gentler.
 *
 * ## Subscription gating
 *
 * **Voice subscriptions are server-authoritative.** `Mythglade.Propagation`
 * decides recipients per tick; `Mythglade.Audio.LiveKit` pushes
 * UpdateSubscriptions to the SFU. The client does NOT second-guess —
 * if the SFU is forwarding a voice track to this listener, the
 * controller renders it. (Previously there was a belt-and-suspenders
 * `setSubscribed(false)` on out-of-range voice; removed because two
 * authorities for the same decision can disagree, and removing the
 * client gate gives a symmetric ~50ms blip on radius crossings instead
 * of asymmetric in/out behavior.)
 *
 * **Music subscriptions are client-gated.** The server has no
 * proximity rule for music tracks, so this module is the only gate.
 * `updateSpeakerPosition` calls `setSubscribed(false)` when a music
 * speaker walks out of range in `spatial` mode. `setMusicMode` exposes
 * an explicit listener choice (`spatial | tuned-in | muted`).
 *
 * See [docs/guides/spatial-audio-livekit.md](../../../../docs/guides/spatial-audio-livekit.md).
 */

import { Track, type RemoteTrack, type RemoteTrackPublication } from "livekit-client";
import { VoiceVolume } from "./VoiceVolume";

// Falloff tuned in world tiles (matches `ZoneScene` selfX/selfY units).
//
// Voice — conversational range, mirrors the 12-tile server-side
// subscription radius in `Mythglade.Propagation.@voice_say_radius`.
// `refDistance: 2` keeps the speaker at full volume for face-to-face;
// at the server's 12-tile cutoff the inverse curve has already faded
// to ~11% (rolloffFactor 2), inaudible enough that the server unsub
// blip on top is unnoticeable.
const VOICE_FALLOFF = {
  refDistance: 2,
  maxDistance: 10,
  rolloffFactor: 2,
} as const;

// Music — moderate range, no server-side cutoff (one host per zone
// makes per-listener gating not worth the round-trip). Music carries
// roughly 2× as far as voice; central hosts reach the whole ~16-tile
// zone at low volume, walking to the jukebox boosts presence.
const MUSIC_FALLOFF = {
  refDistance: 4,
  maxDistance: 20,
  rolloffFactor: 1.5,
} as const;

export type ListenerMusicMode = "spatial" | "tuned-in" | "muted";

export interface SpatialAudioControllerOptions {
  audioContext?: AudioContext;
  /** Optional override per kind; fall back to the constants above. */
  voiceFalloff?: { refDistance: number; maxDistance: number; rolloffFactor: number };
  musicFalloff?: { refDistance: number; maxDistance: number; rolloffFactor: number };
}

interface RemoteEntry {
  publication: RemoteTrackPublication;
  panner: PannerNode;
  audio: HTMLAudioElement;
  kind: "voice" | "music";
  /**
   * For music only — current listener mode. Spatial = follow distance;
   * tuned-in = bypass distance; muted = unsubscribe.
   */
  musicMode: ListenerMusicMode;
}

export class SpatialAudioController {
  private readonly audioCtx: AudioContext;
  private readonly masterGain: GainNode;
  private readonly unsubscribeVolume: () => void;
  private readonly remotes = new Map<string, RemoteEntry>();
  private readonly voiceFalloff: { refDistance: number; maxDistance: number; rolloffFactor: number };
  private readonly musicFalloff: { refDistance: number; maxDistance: number; rolloffFactor: number };

  /** Self world position; set every frame from `ZoneScene`. */
  selfX = 0;
  selfY = 0;

  constructor(opts: SpatialAudioControllerOptions = {}) {
    this.audioCtx = opts.audioContext ?? new AudioContext();
    this.voiceFalloff = opts.voiceFalloff ?? VOICE_FALLOFF;
    this.musicFalloff = opts.musicFalloff ?? MUSIC_FALLOFF;

    // Master gain between every per-remote PannerNode and the
    // destination. Driven by the Voice & Music slider in AudioControls.
    // Set to 0 by the mute toggle.
    this.masterGain = this.audioCtx.createGain();
    this.masterGain.connect(this.audioCtx.destination);
    this.unsubscribeVolume = VoiceVolume.subscribe((gain) => {
      this.masterGain.gain.value = gain;
    });
  }

  /**
   * Attach a remote audio track. Builds a per-track `MediaStream` →
   * `<audio>` → `MediaElementAudioSourceNode` → `PannerNode` →
   * destination chain. The hidden `<audio>` element is required by
   * Chrome to satisfy the autoplay policy gesture chain even when
   * audio is routed through WebAudio.
   *
   * Returns the trackSid for bookkeeping.
   */
  attachRemote(
    speakerKey: string,
    track: RemoteTrack,
    publication: RemoteTrackPublication,
  ): string {
    if (this.remotes.has(speakerKey)) {
      // Replacement (PTT cycle, track re-publish). Tear down first.
      this.detachRemote(speakerKey);
    }

    // Audio routing chain combines two requirements:
    //   1. LiveKit needs `track.attach(element)` to mark the track as
    //      actively rendered. Without it the underlying MediaStreamTrack
    //      may stay `muted=true` (adaptiveStream-pauses behavior) and
    //      `createMediaStreamSource` produces a permanently-silent
    //      source on Chrome.
    //   2. The WebAudio graph must be the only audible path so the
    //      "Voice & Music" slider's masterGain controls volume.
    //
    // Solution: attach the element so packets flow, but set
    // `element.volume = 0` so OS output is silent. `muted = true`
    // would also silence OS output, but Chrome may skip decoding on
    // muted elements and starve the MediaElementSource feeding the
    // WebAudio graph. `volume = 0` keeps the decoder running.
    if (this.audioCtx.state !== "running") {
      void this.audioCtx.resume();
    }

    const audio = document.createElement("audio");
    audio.autoplay = true;
    track.attach(audio);
    audio.volume = 0;
    document.body.appendChild(audio);

    const source = this.audioCtx.createMediaElementSource(audio);
    const panner = this.audioCtx.createPanner();
    panner.panningModel = "HRTF";
    // `inverse` gives a natural-feeling 1/r falloff — sharp near the
    // speaker, gentle at distance. Falloff parameters per kind are
    // tuned in tile units in the constants above.
    panner.distanceModel = "inverse";

    const kind: "voice" | "music" =
      publication.source === Track.Source.Microphone ? "voice" : "music";

    const falloff = kind === "voice" ? this.voiceFalloff : this.musicFalloff;
    panner.refDistance = falloff.refDistance;
    panner.maxDistance = falloff.maxDistance;
    panner.rolloffFactor = falloff.rolloffFactor;

    source.connect(panner).connect(this.masterGain);

    this.remotes.set(speakerKey, {
      publication,
      panner,
      audio,
      kind,
      musicMode: kind === "music" ? "spatial" : "spatial",
    });

    console.log(`[voice] spatial attach kind=${kind} key=${speakerKey} sid=${publication.trackSid}`);

    void source;
    return publication.trackSid;
  }

  /** Move a remote speaker; silently no-ops if the speaker isn't attached. */
  updateSpeakerPosition(speakerKey: string, x: number, y: number): void {
    const entry = this.remotes.get(speakerKey);
    if (!entry) return;

    const dx = x - this.selfX;
    const dy = y - this.selfY;
    entry.panner.positionX.value = dx;
    entry.panner.positionY.value = 0;
    entry.panner.positionZ.value = dy;

    // Voice subscriptions are server-authoritative — see module
    // docstring. Music has no server gate, so we gate here when the
    // listener has chosen `spatial` mode.
    if (entry.kind === "music" && entry.musicMode === "spatial") {
      const dist = Math.hypot(dx, dy);
      const inRange = dist <= entry.panner.maxDistance;
      void entry.publication.setSubscribed(inRange);
    }
  }

  /**
   * Music-only — switch listener mode for a track. Mode mapping
   * matches the existing `SpatialMusicLayer` semantics:
   *
   *   * `spatial`   — distance gate + PannerNode falloff
   *   * `tuned-in`  — bypass distance, flat gain
   *   * `muted`     — unsubscribe
   */
  setMusicMode(speakerKey: string, mode: ListenerMusicMode): void {
    const entry = this.remotes.get(speakerKey);
    if (!entry || entry.kind !== "music") return;

    entry.musicMode = mode;

    switch (mode) {
      case "spatial":
        // Restore the configured falloff (inverse 1/r curve + tuned
        // rolloffFactor). Tuned-in mode below flattens gain by zeroing
        // rolloffFactor, so we reset it here when switching back.
        entry.panner.distanceModel = "inverse";
        entry.panner.rolloffFactor = this.musicFalloff.rolloffFactor;
        void entry.publication.setSubscribed(true);
        break;
      case "tuned-in":
        // Flat gain — mimics a "now playing" stream. Setting
        // rolloffFactor to 0 makes the inverse curve return refDistance /
        // refDistance = 1 at every distance, so the listener hears the
        // host at full volume regardless of position.
        entry.panner.distanceModel = "inverse";
        entry.panner.rolloffFactor = 0;
        void entry.publication.setSubscribed(true);
        break;
      case "muted":
        void entry.publication.setSubscribed(false);
        break;
    }
  }

  detachRemote(speakerKey: string): void {
    const entry = this.remotes.get(speakerKey);
    if (!entry) return;
    try {
      entry.audio.remove();
      entry.panner.disconnect();
    } catch {
      // best-effort
    }
    this.remotes.delete(speakerKey);
    console.log(`[voice] spatial detach kind=${entry.kind} key=${speakerKey}`);
  }

  destroy(): void {
    this.unsubscribeVolume();
    for (const key of [...this.remotes.keys()]) this.detachRemote(key);
    try { this.masterGain.disconnect(); } catch { /* best-effort */ }
    void this.audioCtx.close();
  }
}
